import React, { useState, useEffect, useRef } from "react";
import {
  IoAddCircleSharp,
  IoArrowBackOutline,
  IoArrowForward,
  IoStar,
} from "react-icons/io5";
import { RiHotelLine } from "react-icons/ri";
import { GoHome } from "react-icons/go";
import { IoMdTv } from "react-icons/io";
import Navbar from "../../../components/Navbar";
import { FaAngleDown, FaAngleUp, FaWifi } from "react-icons/fa";
import { LiaAngleDownSolid, LiaAngleUpSolid } from "react-icons/lia";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import HostImage from "../../../helper/images/host-image.png";
import { MdLocalHotel } from "react-icons/md";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "../../../components/Footer";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getHotelById,
  getHotelActivities,
  getAvailableRooms,
  getBookingPrice,
  bookHotel,
} from "../../../api/user/hotels";
import { getAvailableOffers } from "../../../api/user/restaurants";
import { useLocation } from "react-router-dom";
import { MdStar, MdStarHalf, MdStarOutline, MdDelete } from "react-icons/md";
import {
  MdWifi,
  MdPool,
  MdFitnessCenter,
  MdSpa,
  MdLocalParking,
  MdRestaurant,
  MdLocalBar,
  MdAcUnit,
  MdPets,
} from "react-icons/md";
import Location from "../../Home/Location";
import TopHotelsAndRestaurants from "../../Home/TopHotelsAndRestaurants";
import { useAuth } from "../../../context/AuthContext";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { launchConfetti } from "../../../helper/partyEffect";
import Pagination from "../../../components/common/Pagination";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const HotelDetail = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hotelId } = location.state || {};
  const [formData, setFormData] = useState({
    hotelId: hotelId,
    checkInDate: new Date(),
    checkOutDate: new Date(new Date().setDate(new Date().getDate() + 3)),
    guests: 2,
    notes: "",
    activities: [],
    roomIds: [],
    isPayLater: false,
  });
  const { token } = useAuth();
  const [showRooms, setShowRooms] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [guestCount, setGuestCount] = useState(2);
  const debounceTimeout = useRef(null);
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["hotel-detail", hotelId],
    queryFn: () => getHotelById(hotelId),
    enabled: !!hotelId,
  });

  const hotel = data?.data;

  const { data: activitiesData, isLoading: isActivitiesLoading } = useQuery({
    queryKey: ["activities", hotelId],
    queryFn: () => getHotelActivities(hotelId),
  });

  const activities = activitiesData?.data?.activities;

  const { data: offersData, isLoading: offersLoading } = useQuery({
    queryKey: ["offers", hotelId],
    queryFn: () => getAvailableOffers({ hotelId }),
    keepPreviousData: true,
  });

  const offers = offersData?.data?.offers || [];

  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate && hotelId && showRooms) {
      fetchAvailableRooms({
        hotelId,
        page,
        limit: 5,
        roomCapacity: formData.roomCapacity,
        checkInDate: formData.checkInDate,
        checkOutDate: formData.checkOutDate,
      });
    }
  }, [page]);

  const {
    mutate: fetchAvailableRooms,
    data: availableRoomsData,
    isPending: isRoomsLoading,
    isSuccess: isRoomsLoaded,
  } = useMutation({
    mutationFn: (variables) => getAvailableRooms(variables),
    onSuccess: (data) => {
      setPagination(data?.data?.pagination);
      setShowRooms(true);
    },
  });

  const {
    mutate: fetchBookingPrice,
    data: bookingPriceData,
    isPending: isBookingPriceLoading,
    isSuccess: isBookingPriceLoaded,
  } = useMutation({
    mutationFn: (data) => getBookingPrice(data),
    onSuccess: () => {
      setShowRooms(true);
    },
  });

  const bookingPrice = bookingPriceData?.data?.priceBreakdown;

  const {
    mutate: bookHotelMutation,
    isPending: isBookingHotelLoading,
    isSuccess: isBookingHotelLoaded,
  } = useMutation({
    mutationFn: (data) => bookHotel(data),
    onSuccess: ({ data }) => {
      if (data?.paymentUrl) {
        const { paymentUrl } = data;
        if (paymentUrl) {
          window.open(paymentUrl, "_blank");
        } else {
          toast.error("Checkout URL not found.");
        }
      } else {
        toast.success(t("hotelDetail").bookingSuccess);
      }

      setShowPriceBreakdown(false);
      setFormData({
        hotelId: hotelId,
        checkInDate: new Date(),
        checkOutDate: new Date(new Date().setDate(new Date().getDate() + 3)),
        guests: 2,
        notes: "",
        activities: [],
        roomIds: [],
        isPayLater: false,
      });
      setShowRooms(false);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to get checkout url.";
      toast.error(errorMessage);
    },
  });

  const [openIndex, setOpenIndex] = useState(0);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    if (!formData.roomIds.length) return;
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchBookingPrice({
        hotelId: formData.hotelId,
        checkInDate: formData.checkInDate.toISOString().split("T")[0],
        checkOutDate: formData.checkOutDate.toISOString().split("T")[0],
        guests: formData.guests,
        activities: formData.activities,
        roomIds: formData.roomIds,
        notes: formData.notes,
        offerId: selectedOffer?._id,
      });
    }, 500);

    return () => {
      clearTimeout(debounceTimeout.current);
    };
  }, [
    formData.hotelId,
    formData.checkInDate,
    formData.checkOutDate,
    formData.guests,
    formData.activities,
    formData.roomIds,
    formData.notes,
    selectedOffer,
  ]);

  const facilityIcons = {
    "Free Wi-Fi": MdWifi,
    "Swimming Pool": MdPool,
    "Fitness Center": MdFitnessCenter,
    Spa: MdSpa,
    Parking: MdLocalParking,
    Restaurant: MdRestaurant,
    Bar: MdLocalBar,
    "Air Conditioning": MdAcUnit,
    "Pets Allowed": MdPets,
  };

  const hotelWithType = {
    ...hotel,
    type: "hotel",
  };

  const handleCheckAvailability = () => {
    if (!token) {
      navigate("/login");
      return;
    }
    const formattedData = {
      ...formData,
      page,
      limit: 5,
      checkInDate: formData.checkInDate.toISOString().split("T")[0],
      checkOutDate: formData.checkOutDate.toISOString().split("T")[0],
      roomCapacity: guestCount,
      status: "available",
    };

    fetchAvailableRooms(formattedData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleActivityToggle = (activityId) => {
    setFormData((prev) => {
      const activityIndex = prev.activities.indexOf(activityId);
      let newActivities = [...prev.activities];

      if (activityIndex === -1) {
        newActivities.push(activityId);
      } else {
        newActivities.splice(activityIndex, 1);
      }

      return {
        ...prev,
        activities: newActivities,
      };
    });
  };

  const handleBookRoom = (roomId) => {
    setFormData((prev) => ({
      ...prev,
      roomIds: [...new Set([...prev.roomIds, roomId])],
    }));
  };


  
  const handleCheckout = () => {
    if (!formData.roomIds.length) return;

    fetchBookingPrice({
      hotelId: formData.hotelId,
      checkInDate: formData.checkInDate.toISOString().split("T")[0],
      checkOutDate: formData.checkOutDate.toISOString().split("T")[0],
      guests: formData.guests,
      activities: formData.activities,
      roomIds: formData.roomIds,
      notes: formData.notes,
      offerId: selectedOffer?._id,
    });

    setShowPriceBreakdown(true);
  };

  const handleApplyOffer = (offerId) => {
    const selected = offers.find((offer) => offer._id === offerId);

    if (selected) {
      setSelectedOffer(selected);
    }
  };

  const payAtCheckOut = () => {
    Swal.fire({
      title:
        `${t("hotelDetail").bookingConfirmation} ${t("hotelDetail").payLaterConfirmation}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("hotelDetail").yes,
      cancelButtonText: t("hotelDetail").no,
    }).then((result) => {
      if (result.isConfirmed) {
        bookHotelMutation({
          hotelId: formData.hotelId,
          checkInDate: formData.checkInDate.toISOString().split("T")[0],
          checkOutDate: formData.checkOutDate.toISOString().split("T")[0],
          guests: formData.guests,
          activities: formData.activities,
          roomIds: formData.roomIds,
          notes: formData.notes,
          isPayLater: true,
          offerId: selectedOffer?._id,
        });
      }
    });
  };

  const payNow = () => {
    Swal.fire({
      title:
        `${t("hotelDetail").bookingConfirmation} ${t("hotelDetail").payNowConfirmation}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("hotelDetail").yes,
      cancelButtonText: t("hotelDetail").no,
    }).then((result) => {
      if (result.isConfirmed) {
        bookHotelMutation({
          hotelId: formData.hotelId,
          checkInDate: formData.checkInDate.toISOString().split("T")[0],
          checkOutDate: formData.checkOutDate.toISOString().split("T")[0],
          guests: formData.guests,
          activities: formData.activities,
          roomIds: formData.roomIds,
          notes: formData.notes,
          isPayLater: false,
          offerId: selectedOffer?._id,
        });
      }
    });
  };

  const handleRemoveRoom = (roomId) => {
    setFormData((prev) => ({
      ...prev,
      roomIds: prev.roomIds.filter((id) => id !== roomId),
    }));
  };


  if (isLoading) {
   
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  

  return (
    <>
      <div className=" lg:pb-[100px] md:pb-[70px] pb-[30px]">
        <div className="container">
          {hotel?.images && hotel.images.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-[605px_1fr] md:grid-cols-2 gap-[19px] mt-[36px] mb-[40px]">
              <div>
                <img
                  src={`${hotel.images[0]}`}
                  alt="hotel"
                  className="rounded-tl-[10px] rounded-bl-[10px] w-full h-full object-cover"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
                {hotel.images.slice(1, 5).map((image, index) => (
                  <div key={index}>
                    <img
                      src={`${image}`}
                      alt={`hotel-${index}`}
                      className={`w-full h-full object-cover ${
                        index === 1 || index === 3
                          ? "rounded-tr-[10px] rounded-br-[10px]"
                          : ""
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p>{t("hotelDetail").noImages}</p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_516px]  gap-[42px] mt-[40px]">
            <div>
              <h2 className="md:text-[25px] text-[20px] font-semibold text-black mb-[10px] leading-[25px]">
                {hotel?.name}
              </h2>
              <p className="text-lg text-black mb-5 leading-[22px]">
                {hotel?.totalBedrooms} bedrooms, {hotel?.washrooms} bathroom
              </p>
              {typeof hotel?.rating === "number" && !isNaN(hotel.rating) && (
                <div className="flex flex-wrap gap-[14px] w-full mb-[20px]">
                  <p className="text-lg text-black font-medium leading-[22px]">
                    {t("hotelDetail").reviews}
                    {hotel.rating} / 5
                  </p>
                  <div className="flex items-center gap-[2px]">
                    {Array(Math.floor(hotel.rating))
                      .fill()
                      .map((_, index) => (
                        <MdStar
                          key={`full-${index}`}
                          size={21}
                          className="text-[#FF9D00]"
                        />
                      ))}

                    {hotel.rating % 1 !== 0 && (
                      <MdStarHalf size={21} className="text-[#FF9D00]" />
                    )}

                    {Array(5 - Math.ceil(hotel.rating))
                      .fill()
                      .map((_, index) => (
                        <MdStarOutline
                          key={`empty-${index}`}
                          size={21}
                          className="text-[#FF9D00]"
                        />
                      ))}
                  </div>
                </div>
              )}

              <div className="pt-[26px] border-t border-[#D9D9D9] pb-5">
                <div
                  dangerouslySetInnerHTML={{ __html: hotel?.description }}
                  className="text-[#333] text-[16px] leading-[26px]"
                />
              </div>
              <div className="pb-5">
                <h2 className="text-xl text-black font-semibold mb-5">
                  {t("hotelDetail").whatOffers}
                </h2>

                <div className="grid md:grid-cols-2 md:gap-[24px] gap-3">
                  {hotel?.facilities?.map((facility, index) => {
                    const Icon = facilityIcons[facility];

                    return (
                      <div
                        className="grid grid-cols-[24px_1fr] gap-[14px] mb-[15px]"
                        key={index}
                      >
                        {Icon ? (
                          <Icon
                            className="text-[20px] text-black"
                            stroke="1.5"
                          />
                        ) : (
                          <div className="text-black text-[20px]">•</div>
                        )}
                        <h3 className="text-black text-[18px] leading-[22px]">
                          {facility}
                        </h3>
                      </div>
                    );
                  })}
                </div>

                {hotel?.activityBookingEnabled && (
                  <div className="pt-[45px]">
                    <h2 className="text-xl text-black font-semibold mb-5">
                      {t("hotelDetail").availableActivities}
                    </h2>
                    <p className="te-lg leading-[30px]">
                      {t("hotelDetail").activityDescription}
                    </p>
                    <div className="border border-[#DDDDDD] rounded-[10px] shadow-sm mt-5 px-[15px]">
                      {activities?.map((activity, index) => (
                        <div
                          key={index}
                          className="border-t first:border-t-0 border-[#D9D9D9]"
                        >
                          <button
                            onClick={() => toggle(index)}
                            className="w-full text-left  pt-4 pb-[15px] flex justify-between items-center font-medium"
                          >
                            <span className="text-lg font-medium ">
                              {activity.name}
                            </span>
                            <span>
                              {openIndex === index ? (
                                <LiaAngleUpSolid
                                  size={20}
                                  className="text-black"
                                />
                              ) : (
                                <LiaAngleDownSolid
                                  size={20}
                                  className="text-black"
                                />
                              )}
                            </span>
                          </button>
                          {openIndex === index && (
                            <div className=" pb-4 text-base text-black">
                              <p className="pb-[10px]">
                                {activity.description}
                              </p>
                              <div className="flex flex-wrap gap-[30px] text-base pb-[20px]">
                                <div>
                                  <span className="text-black  font-medium">
                                    {t("hotelDetail").category}
                                  </span>{" "}
                                  {activity.category}
                                </div>
                                <div>
                                  <span className="text-black  font-medium">
                                    {t("hotelDetail").price}
                                  </span>{" "}
                                  {activity.price}
                                </div>
                                <div>
                                  <span className="text-black  font-medium">
                                    {t("hotelDetail").duration}
                                  </span>{" "}
                                  {activity.duration}
                                </div>
                                <div>
                                  <span className="text-black  font-medium">
                                    {t("hotelDetail").availableSlots}
                                  </span>{" "}
                                  {activity.slots}
                                </div>
                              </div>

                              <button
                                onClick={() =>
                                  handleActivityToggle(activity._id)
                                }
                                className={`font-semibold grid grid-cols-[auto_1fr] gap-[11px] items-center ${
                                  formData.activities.includes(activity._id)
                                    ? "text-[#FFA808]"
                                    : "text-[#4CAF50]"
                                }`}
                                disabled={!token}
                              >
                                <IoAddCircleSharp size={16} />
                                {formData.activities.includes(activity._id)
                                  ? "Activity Added"
                                  : "Add Activity"}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <textarea
                  className="w-full h-[200px] border border-[#DDDDDD] rounded-[10px] p-[24px] shadow-sm focus:outline-none focus:border-[#4CAF50]"
                  placeholder='Check-in/check-out preferences (e.g., "Late check-in at 10 PM")'
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>
            <div className="h-max sticky top-[100px]">
              <div className="pt-[23px] pb-[36px] px-5 rounded-[10px] side-box">
                <h2 className="text-[22px] font-medium mb-5">
                  {t("hotelDetail").addDates}
                </h2>
                <div className="rounded-[10px] border border-[#D9D9D9] mb-[17px] block">
                  <div className="grid grid-cols-2 border-b border-[#D9D9D9]">
                    <div className="relative py-[12px] px-[20px]">
                      <h3 className="text-base font-medium leading-5 mb-[7px] uppercase">
                        {t("hotelDetail").checkin}
                      </h3>
                      <DatePicker
                        selected={formData.checkInDate}
                        onChange={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            checkInDate: date,
                          }))
                        }
                        dateFormat="dd MMM yyyy"
                        className="text-[13px] text-black bg-transparent focus:outline-none cursor-pointer"
                      />
                    </div>

                    <div className="relative border-l border-[#D9D9D9] py-[12px] px-[20px]">
                      <h3 className="text-base font-medium leading-5 mb-[7px] uppercase">
                        {t("hotelDetail").checkout}
                      </h3>
                      <DatePicker
                        selected={formData.checkOutDate}
                        onChange={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            checkOutDate: date,
                          }))
                        }
                        dateFormat="dd MMM yyyy"
                        className="text-[13px] text-black bg-transparent focus:outline-none cursor-pointer"
                        minDate={formData.checkInDate}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-[20px] px-[20px]">
                    <h3 className="text-base font-medium uppercase leading-[20px]">
                      {t("hotelDetail").guests}
                    </h3>
                    <input
                      type="number"
                      name="guests"
                      value={guestCount}
                      onChange={(e) => setGuestCount(e.target.value)}
                      className="text-[13px] text-black focus-visible:outline-none text-right w-[50px]"
                      min="1"
                    />
                  </div>
                </div>

                <button
                  onClick={handleCheckAvailability}
                  // disabled={!token}
                  className="w-full inline-block rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold 2xl:p-[14px] 2xl:px-5 p-3 green-shadow hover:bg-[#209b25]"
                >
                  {t("hotelDetail").checkAvailability}
                </button>
              </div>
              {showRooms && (
                <div className="mt-10 p-5 border rounded shadow bg-white">
                  <h2 className="text-xl font-semibold mb-4">
                    {t("hotelDetail").availableRooms}
                  </h2>

                  {isRoomsLoading ? (
                    <p>{t("hotelDetail").loading}</p>
                  ) : (
                    availableRoomsData?.data?.rooms?.map((room) => {
                      const isSelected = formData.roomIds.includes(room._id);

                      return (
                        <div key={room._id} className="mb-4 p-4 border rounded">
                          <div className="flex justify-between items-center">
                            <div>
                              <h3 className="font-semibold text-lg">
                                {room.roomType} - #{room.roomNumber}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {room.roomSize} • {room.bedType}
                              </p>
                              <p className="text-sm">
                                {t("hotelDetail").capacity} {room.roomCapacity}{" "}
                                People{" "}
                              </p>
                              <p className="text-sm">
                                {t("hotelDetail").price} ${room.price} / night
                              </p>
                              <p className="text-sm text-gray-600 mt-2">
                                {room.description}
                              </p>
                              <p className="text-sm mt-2">
                                {t("hotelDetail").facilities}{" "}
                                {room.facilities.join(", ")}
                              </p>
                            </div>

                            {isSelected ? (
                              <div className="flex gap-2">
                                <button
                                  disabled
                                  className="bg-gray-300 text-gray-600 font-semibold px-4 py-2 rounded cursor-not-allowed"
                                >
                                  {t("hotelDetail").added}
                                </button>
                                <button
                                  onClick={() => handleRemoveRoom(room._id)}
                                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded font-semibold flex items-center gap-2"
                                >
                                  <MdDelete size={16} />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleBookRoom(room._id)}
                                className="bg-[#4CAF50] hover:bg-[#388e3c] text-white px-4 py-2 rounded font-semibold"
                              >
                                {t("hotelDetail").book}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}

                  {pagination?.totalPages > 1 && (
                    <div className="mt-6">
                      <Pagination
                        page={pagination.page}
                        totalPages={pagination.totalPages}
                        setPage={setPage}
                      />
                    </div>
                  )}

                  {formData.roomIds.length > 0 && (
                    <div className="mt-6 p-4 bg-green-50  rounded">
                      <button
                        onClick={handleCheckout}
                        className="w-full bg-[#4CAF50] hover:bg-[#388e3c] text-white font-bold py-3 rounded shadow-md"
                      >
                        {t("hotelDetail").proceedCheckout}
                      </button>
                    </div>
                  )}

                  {showPriceBreakdown && bookingPrice && (
                    <div className="mt-4 p-4 border rounded bg-gray-50">
                      <h4 className="font-semibold mb-2 text-gray-800">
                        {t("hotelDetail").priceBreakdown}
                      </h4>
                      <ul className="text-sm text-gray-700 mb-4">
                        <li>
                          <strong>{t("hotelDetail").nights}</strong>{" "}
                          {bookingPrice.numberOfNights}
                        </li>
                        <li>
                          <strong>{t("hotelDetail").roomSubtotal}</strong> ₾
                          {bookingPrice.roomSubtotal}
                        </li>
                        <li>
                          <strong>{t("hotelDetail").activityTotal}</strong> ₾
                          {bookingPrice.activityTotal}
                        </li>
                        <li>
                          <strong>{t("hotelDetail").discount}</strong> ₾
                          {bookingPrice.discountAmount}
                        </li>
                        <li>
                          <strong> {t("hotelDetail").totalAmount}</strong> ₾
                          {bookingPrice.totalAmount}
                        </li>
                      </ul>

                      {offers?.length > 0 && (
                        <button
                          onClick={() => setIsPopupOpen(true)}
                          className="text-green-600 underline mt-3 mb-5 "
                        >
                          {t("hotelDetail").viewOffers}
                        </button>
                      )}

                      {bookingPrice?.payAtCheckOut ? (
                        <div className="flex gap-4">
                          <button
                            onClick={payNow}
                            className="flex-1 bg-[#4CAF50] hover:bg-[#388e3c] text-white font-bold py-2 rounded"
                          >
                            {t("hotelDetail").payNow}
                          </button>
                          <button
                            onClick={payAtCheckOut}
                            className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 rounded"
                          >
                            {t("hotelDetail").payCheckout}
                          </button>
                        </div>
                      ) : (
                        <div className="flex">
                          <button
                            onClick={payNow}
                            className="flex-1 bg-[#4CAF50] hover:bg-[#388e3c] text-white font-bold py-2 rounded"
                          >
                            {t("hotelDetail").payNow}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#FFF3EF] lg:py-[100px] md:py-[70px] py-[30px] ">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-[42px] grid-cols-1">
            <div className="self-start">
              <h2 className="lg:text-[30px] text-[25px] font-semibold text-black lg:mb-[15px] mb-[15px]">
                {t("hotelDetail").whereYoullBe}
              </h2>
              <p className="text-lg mb-[20px]">{hotel?.address}</p>
              <div className="w-full md:h-[400px] h-[300px] rounded-[10px] overflow-hidden">
                <Location data={[hotelWithType]} />
              </div>
            </div>
            <div className="self-center">
              {/* <h2 className="lg:text-[30px] text-[22px] font-semibold text-black mb-[15px] ">
                {t("hotelDetail").meetHost}
              </h2>
              <div className="bg-white rounded-[10px] mb-[30px] px-[23px] py-[30px] max-w-[534px] grid grid-cols-[auto_1fr] gap-[20px]">
                <img
                  src={`${hotel?.ownerId?.profilePicture}`}
                  className="md:max-w-[100px] md:h-[100px] max-w-[50px] h-[50px] object-cover rounded-full bottom-1 border-[#DDDDDD]"
                  alt=""
                />
                <div>
                  <div className="flex items-center gap-[10px] justify-between mb-[13px]">
                    <h3 className="text-[22px] font-medium">
                      {hotel?.ownerId?.name}
                    </h3>
                    <div className="flex items-center w-auto max-w-[60px]">
                      <MdStar size={11} className="text-[#F0AD4E]" />
                      <MdStar size={11} className="text-[#F0AD4E]" />
                      <MdStar size={11} className="text-[#F0AD4E]" />
                      <MdStar size={11} className="text-[#F0AD4E]" />
                      <MdStar size={11} className="text-[#F0AD4E]" />
                    </div>
                  </div>
                  <div className="flex items-center gap-[10px]">
                    <RiHotelLine size={24} className="text-[#7B7B7B]" />
                    <p className="text-[#18181B] text-[15px]">
                      {t("hotelDetail").stars}
                    </p>
                  </div>
                  <div className="flex items-center gap-[10px] mt-[4px]">
                    <MdLocalHotel size={24} className="text-[#7B7B7B]" />
                    <p className="text-[#18181B] text-[15px]">
                      {t("hotelDetail").yearsHosting}
                    </p>
                  </div>
                  <button className="md:w-auto w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold 2xl:p-[14px] 2xl:px-[25px] p-3 green-shadow hover:bg-[#209b25] mt-5">
                    {t("hotelDetail").messageHost}
                  </button>
                </div>
              </div> */}
              <div className="md:p-[30px] p-5 bg-[#D4EFD5] rounded-[10px] w-full">
                <h2 className="lg:text-[30px] text-[25px] font-semibold text-black lg:mb-[15px] mb-[15px]">
                  {t("hotelDetail").thingsToKnow}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:mt-[24px] mt-3 items-start">
                  <div>
                    <h3 className="md:text-[22px] text-base font-medium md:mb-[15px] mb-[15px] text-black">
                      {t("hotelDetail").houseRules}
                    </h3>
                    <ul className="md:text-[18px] text-sm text-[#18181B] leading-[30px] list-none pl-0">
                      <li>{t("hotelDetail").checkinAfter}</li>
                      <li>{t("hotelDetail").maxGuests}</li>
                      <li>{t("hotelDetail").noPets}</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="md:text-[22px] text-base font-medium md:mb-[15px] mb-[15px] text-black">
                      {t("hotelDetail").safetyProperty}
                    </h3>
                    <ul className="md:text-[18px] text-sm text-[#18181B] leading-[30px] list-none pl-0">
                      <li>{t("hotelDetail").carbonMonoxide}</li>
                      <li>{t("hotelDetail").smokeAlarm}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TopHotelsAndRestaurants
        title="Related Hotels"
        show={false}
        prefer="hotels"
      />

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full rounded-[20px] bg-white sm:p-[30px] p-[15px]">
          <h2 className="text-[24px] font-bold mb-[25px] text-gray-800 text-center">
            {t("hotelDetail").availableDiscountOffers}
          </h2>

          <div className="space-y-6 max-h-[450px] overflow-y-auto px-1">
            {offers?.length > 0 ? (
              offers.map((offer) => (
                <div
                  key={offer._id}
                  className="border border-gray-200 bg-gray-50 rounded-[12px] p-4 flex flex-col sm:flex-row gap-4 shadow-md hover:shadow-lg transition-shadow duration-200"
                >
                  {/* Image */}
                  <img
                    src={`${offer.image}`}
                    alt={offer.title}
                    className="w-full sm:w-[140px] h-[140px] object-cover rounded-[10px] border border-gray-300"
                  />

                  {/* Offer Details */}
                  <div className="flex flex-col justify-between flex-grow">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="text-[18px] font-semibold text-gray-900">
                          {offer.title}
                        </h3>
                        <span className="bg-red-500 text-white text-[14px] font-bold px-3 py-1 rounded-full shadow-sm">
                          -{offer.discountPercentage}%
                        </span>
                      </div>
                      <p className="text-[15px] text-gray-600 mt-1">
                        {offer.description}
                      </p>
                    </div>

                    <div className="flex justify-end gap-3 mt-5">
                      {selectedOffer?._id === offer?._id ? (
                        <button
                          className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm"
                          onClick={() => {
                            setSelectedOffer(null);
                            setIsPopupOpen(false);
                          }}
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm"
                          onClick={() => {
                            launchConfetti();
                            handleApplyOffer(offer._id);
                            setIsPopupOpen(false);
                          }}
                        >
                          {t("hotelDetail").apply}
                        </button>
                      )}
                    </div>

                    {/* <div className="flex justify-end gap-3 mt-5">
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm"
                        onClick={() => {
                          launchConfetti()
                          handleApplyOffer(offer._id);
                          setIsPopupOpen(false);
                        }}
                      >
                        Apply Offer
                      </button>
                      <button
                        className="border border-gray-400 text-gray-700 hover:text-black px-5 py-2 rounded-lg text-sm font-medium"
                        onClick={() => {
                          setSelectedOffer(null);
                          setIsPopupOpen(false);
                        }}
                      >
                        Remove
                      </button>
                    </div> */}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 text-base">
                {t("hotelDetail").noOffers}
              </p>
            )}
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default HotelDetail;
